// src/portal-app/team/EmployeePayrollTab.js
//
// W4 Task 3 — Payroll section tab within EmployeeDetailScreen.
// Receives `employeeId` (number) and `currency` (string) as props.
//
// Zones:
//   CurrentPeriodCard — active (non-closed) pay run line item for employee
//   PayHistorySection — timeline of payslip summaries (read-only)
//   LedgerSection     — advances (last 5) | allowances (last 5) | bonuses (last 5)
//                       Advances + Bonuses have "Add" → bottom sheet form
//
// Services used (unchanged):
//   payrunService.list()
//   employeeV2Service.listPayslips(empId)
//   employeeService.getEmployeeAdvances(empId)
//   employeeService.getEmployeeAllowances(empId)
//   employeeV2Service.listBonuses(empId)
//   employeeV2Service.createBonus(empId, payload)
//   employeeService.createSalaryAdvance(data)
//   sessionService.getActiveSessions()
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import {
    payrunService,
    employeeV2Service,
    employeeService,
    sessionService,
} from '../../services/api';
import { StatusBadge } from '../operations/kit';
import { SurfaceStates, fmtMoney } from '../insights/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNav } from '../nav/NavStack';

/* ------------------------------------------------------------------ */
/* Stylesheet                                                           */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- payroll tab layout ---- */
.ept-wrap{width:100%;box-sizing:border-box;padding:0 0 40px;}

/* ---- section header ---- */
.ept-section-hdr{display:flex;align-items:center;justify-content:space-between;
  margin:16px 0 8px;}
.ept-section-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-mutedLight);}

/* ---- card ---- */
.ept-card{padding:16px;margin-bottom:10px;}
.ept-card-header{display:flex;align-items:center;justify-content:space-between;
  margin-bottom:12px;flex-wrap:wrap;gap:8px;}
.ept-card-title{font-size:13px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);}

/* ---- period card grid ---- */
.ept-period-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));
  gap:10px;}
.ept-period-field-label{font-size:11px;font-weight:600;text-transform:uppercase;
  letter-spacing:.6px;color:var(--c-mutedLight);margin-bottom:3px;}
.ept-period-field-value{font-size:15px;font-weight:700;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.ept-period-field-value.ept-net{font-size:18px;color:var(--c-green);}

/* ---- no period ---- */
.ept-no-period{padding:24px 16px;text-align:center;}
.ept-no-period-icon{opacity:.35;margin-bottom:8px;}
.ept-no-period-msg{font-size:14px;font-weight:600;color:var(--c-textLight);}

/* ---- payslip timeline item ---- */
.ept-history-item{display:flex;align-items:center;justify-content:space-between;
  gap:10px;padding:10px 0;border-bottom:1px solid
  color-mix(in srgb,var(--c-ink) 6%,transparent);}
.ept-history-item:last-child{border-bottom:none;padding-bottom:0;}
.ept-history-left{flex:1;min-width:0;}
.ept-history-period{font-size:12px;font-weight:600;color:var(--c-textLight);}
.ept-history-right{display:flex;flex-direction:column;align-items:flex-end;gap:3px;}
.ept-history-net{font-size:15px;font-weight:700;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.ept-history-outstanding{font-size:11px;font-weight:600;color:var(--c-red);
  font-variant-numeric:tabular-nums;}
.ept-history-paid{font-size:11px;font-weight:600;color:var(--c-success);}

/* ---- empty ledger ---- */
.ept-empty{padding:12px 0;font-size:13px;color:var(--c-mutedLight);
  text-align:center;}

/* ---- ledger item ---- */
.ept-ledger-item{display:flex;align-items:flex-start;justify-content:space-between;
  gap:10px;padding:9px 0;border-bottom:1px solid
  color-mix(in srgb,var(--c-ink) 6%,transparent);}
.ept-ledger-item:last-child{border-bottom:none;padding-bottom:0;}
.ept-ledger-left{flex:1;min-width:0;}
.ept-ledger-desc{font-size:13px;font-weight:600;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ept-ledger-date{font-size:11px;font-weight:500;color:var(--c-textLight);
  margin-top:2px;}
.ept-ledger-amount{font-size:14px;font-weight:700;color:var(--c-ink);
  font-variant-numeric:tabular-nums;white-space:nowrap;}

/* ---- add button ---- */
.ept-add-btn{display:inline-flex;align-items:center;gap:5px;
  min-height:36px;padding:0 12px;border-radius:10px;border:none;
  background:color-mix(in srgb,var(--c-green) 10%,transparent);
  font-family:inherit;font-size:12px;font-weight:700;
  color:var(--c-green);cursor:pointer;
  transition:transform .15s ease,background .15s ease;}
.ept-add-btn:active{transform:scale(.97);}

/* ---- bottom sheet ---- */
.ept-sheet-backdrop{position:fixed;inset:0;z-index:1000;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.ept-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:24px 20px calc(24px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:85vh;overflow-y:auto;}
.ept-sheet-title{font-size:18px;font-weight:800;letter-spacing:-.3px;
  color:var(--c-ink);margin-bottom:16px;}
.ept-field{margin-bottom:14px;}
.ept-field-lbl{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.8px;color:var(--c-textLight);
  margin-bottom:5px;}
.ept-input{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;}
.ept-input:focus{border-color:var(--c-green);}
.ept-err{padding:10px 14px;border-radius:12px;margin-bottom:12px;
  background:color-mix(in srgb,var(--c-red) 8%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-red) 25%,transparent);
  font-size:13px;font-weight:600;color:var(--c-red);}
.ept-actions{display:flex;gap:10px;margin-top:6px;}
.ept-btn-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:color-mix(in srgb,var(--c-surface) 50%,transparent);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;
  transition:transform .15s ease;}
.ept-btn-cancel:active{transform:scale(.97);}
.ept-btn-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:transform .15s ease,opacity .15s ease;}
.ept-btn-save:active{transform:scale(.97);}
.ept-btn-save:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.ept-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:eptSpin .75s linear infinite;}
@keyframes eptSpin{to{transform:rotate(360deg);}}

@media(prefers-reduced-motion:reduce){
  .ept-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'ept-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* SVG Icons                                                            */
/* ------------------------------------------------------------------ */
const PlusIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const CoinIcon = () => (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round"
        className="ept-no-period-icon" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v12M9 9a3 3 0 0 1 6 0c0 2-3 3-3 3s-3 1-3 3a3 3 0 0 0 6 0" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */
function fmtPeriod(start, end) {
    if (!start) return '';
    if (!end) return start;
    return `${start} – ${end}`;
}

/* ------------------------------------------------------------------ */
/* EditSheet wrapper                                                    */
/* ------------------------------------------------------------------ */
function EditSheet({ testId, open, onClose, title, children }) {
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            className="ept-sheet-backdrop"
            role="presentation"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <GlassCard
                veil
                className="ept-sheet"
                data-testid={testId}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="ept-sheet-title">{title}</div>
                {children}
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* CurrentPeriodCard                                                    */
/* ------------------------------------------------------------------ */
function CurrentPeriodCard({ payrunList, employeeId, currency, t, onPushDetail }) {
    // Find the first non-closed pay run that has a line item for this employee
    const allRuns = [...(payrunList.to_run || []), ...(payrunList.runs || [])];
    let activeRun = null;
    let activeLineItem = null;

    for (const run of allRuns) {
        if (run.status === 'closed') continue;
        const items = run.line_items || [];
        const li = items.find((item) => item.employee_id === employeeId);
        if (li) {
            activeRun = run;
            activeLineItem = li;
            break;
        }
    }

    if (!activeRun || !activeLineItem) {
        return (
            <GlassCard veil className="ept-card">
                <div className="ept-card-header">
                    <span className="ept-card-title">{t('current_pay_period', {}, 'Current Pay Period')}</span>
                </div>
                <div className="ept-no-period">
                    <div><CoinIcon /></div>
                    <div className="ept-no-period-msg">{t('no_active_pay_period', {}, 'No active pay period')}</div>
                </div>
            </GlassCard>
        );
    }

    const deductions = (activeLineItem.advance_deduction || 0) +
        (activeLineItem.allowance_deduction || 0) +
        (activeLineItem.other_deductions || 0);

    return (
        <GlassCard
            veil
            className="ept-card"
            data-testid="current-period-card"
            onClick={onPushDetail ? () => onPushDetail(activeRun.pay_run_id) : undefined}
            style={{ cursor: onPushDetail ? 'pointer' : 'default' }}
        >
            <div className="ept-card-header">
                <span className="ept-card-title">{t('current_pay_period', {}, 'Current Pay Period')}</span>
                <StatusBadge
                    status={activeRun.status}
                    label={activeRun.status}
                    tone={activeRun.status === 'paid' ? 'green' : activeRun.status === 'draft' ? 'muted' : 'amber'}
                />
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--c-textLight)', marginBottom: 10 }}>
                {fmtPeriod(activeRun.period_start, activeRun.period_end)}
            </div>
            <div className="ept-period-grid">
                <div>
                    <div className="ept-period-field-label">{t('base_pay', {}, 'Base Pay')}</div>
                    <div className="ept-period-field-value">
                        {fmtMoney(activeLineItem.base_pay || 0, currency)}
                    </div>
                </div>
                <div>
                    <div className="ept-period-field-label">{t('deductions', {}, 'Deductions')}</div>
                    <div className="ept-period-field-value">
                        {deductions > 0 ? `-${fmtMoney(deductions, currency)}` : fmtMoney(0, currency)}
                    </div>
                </div>
                <div>
                    <div className="ept-period-field-label">{t('net_pay', {}, 'Net Pay')}</div>
                    <div className="ept-period-field-value ept-net">
                        {fmtMoney(activeLineItem.net_pay || 0, currency)}
                    </div>
                </div>
            </div>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* PayHistorySection                                                    */
/* ------------------------------------------------------------------ */
function PayHistorySection({ payslips, currency, t }) {
    if (!payslips || payslips.length === 0) {
        return (
            <GlassCard veil className="ept-card">
                <div className="ept-card-title" style={{ marginBottom: 10 }}>
                    {t('pay_history', {}, 'Pay History')}
                </div>
                <div className="ept-empty">{t('no_payslips', {}, 'No payslips yet')}</div>
            </GlassCard>
        );
    }

    return (
        <GlassCard veil className="ept-card" data-testid="pay-history-section">
            <div className="ept-card-title" style={{ marginBottom: 12 }}>
                {t('pay_history', {}, 'Pay History')}
            </div>
            {payslips.map((ps) => {
                const s = ps.snapshot_summary || {};
                const outstanding = s.outstanding || 0;
                return (
                    <div key={ps.payslip_id} className="ept-history-item">
                        <div className="ept-history-left">
                            <div className="ept-history-period">
                                {fmtPeriod(s.period_start, s.period_end)}
                            </div>
                        </div>
                        <div className="ept-history-right">
                            <span className="ept-history-net">
                                {fmtMoney(s.net_pay || 0, currency)}
                            </span>
                            {outstanding > 0
                                ? (
                                    <span className="ept-history-outstanding">
                                        {t('outstanding_label', {}, 'Outstanding')}: {fmtMoney(outstanding, currency)}
                                    </span>
                                )
                                : (
                                    <span className="ept-history-paid">
                                        {t('paid_label', {}, 'Paid')}
                                    </span>
                                )}
                        </div>
                    </div>
                );
            })}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* AdvancesLedger                                                        */
/* ------------------------------------------------------------------ */
function AdvancesLedger({ advances, employeeId, currency, sessions, onCreated, t }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ amount: '', notes: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const autoDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const autoTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

    const handleSave = async () => {
        const parsedAmount = parseFloat(form.amount);
        if (!parsedAmount || parsedAmount <= 0) {
            setError(t('invalid_amount', {}, 'Enter a valid amount'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            const sessionId = sessions && sessions.length > 0 ? sessions[0].session_id : null;
            await employeeService.createSalaryAdvance({
                employee_id: employeeId,
                amount: parsedAmount,
                date: autoDate,
                time: autoTime,
                notes: form.notes || '',
                session_id: sessionId,
            });
            setSaving(false);
            setOpen(false);
            setForm({ amount: '', notes: '' });
            await onCreated();
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const recent = (advances || []).slice(0, 5);

    return (
        <GlassCard veil className="ept-card" data-testid="advances-ledger">
            <div className="ept-card-header">
                <span className="ept-card-title">{t('advances', {}, 'Advances')}</span>
                <button
                    type="button"
                    className="ept-add-btn"
                    data-testid="add-advance-btn"
                    onClick={() => { setError(''); setOpen(true); }}
                    aria-label={t('add_advance', {}, 'Add advance')}
                >
                    <PlusIcon />
                    {t('add', {}, 'Add')}
                </button>
            </div>
            {recent.length === 0
                ? <div className="ept-empty">{t('no_advances', {}, 'No advances recorded')}</div>
                : recent.map((adv) => (
                    <div key={adv.expense_id} className="ept-ledger-item">
                        <div className="ept-ledger-left">
                            <div className="ept-ledger-desc">{adv.notes || t('advance', {}, 'Advance')}</div>
                            <div className="ept-ledger-date">{adv.date}</div>
                        </div>
                        <div className="ept-ledger-amount">{fmtMoney(adv.amount, currency)}</div>
                    </div>
                ))}

            <EditSheet
                testId="advance-sheet"
                open={open}
                onClose={() => setOpen(false)}
                title={t('add_advance', {}, 'Add Advance')}
            >
                {error && <div className="ept-err">{error}</div>}
                <div className="ept-field">
                    <label className="ept-field-lbl">{t('amount', {}, 'Amount')}</label>
                    <input
                        className="ept-input"
                        data-testid="advance-amount"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={form.amount}
                        placeholder="0.00"
                        onChange={(e) => setF('amount', e.target.value)}
                    />
                </div>
                <div className="ept-field">
                    <label className="ept-field-lbl">{t('notes', {}, 'Notes')}</label>
                    <input
                        className="ept-input"
                        data-testid="advance-notes"
                        type="text"
                        value={form.notes}
                        onChange={(e) => setF('notes', e.target.value)}
                    />
                </div>
                <div className="ept-actions">
                    <button type="button" className="ept-btn-cancel" onClick={() => setOpen(false)}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="ept-btn-save"
                        data-testid="advance-save-btn"
                        disabled={saving}
                        onClick={handleSave}
                    >
                        {saving && <span className="ept-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </EditSheet>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* AllowancesLedger                                                      */
/* ------------------------------------------------------------------ */
function AllowancesLedger({ allowances, currency, t }) {
    const recent = (allowances || []).slice(0, 5);

    return (
        <GlassCard veil className="ept-card" data-testid="allowances-ledger">
            <div className="ept-card-header">
                <span className="ept-card-title">{t('allowances', {}, 'Allowances')}</span>
            </div>
            {recent.length === 0
                ? <div className="ept-empty">{t('no_allowances', {}, 'No allowances recorded')}</div>
                : recent.map((al) => (
                    <div key={al.staff_allowance_id} className="ept-ledger-item">
                        <div className="ept-ledger-left">
                            <div className="ept-ledger-desc">
                                {al.product_name}{al.quantity > 1 ? ` × ${al.quantity}` : ''}
                            </div>
                            <div className="ept-ledger-date">{al.date}</div>
                        </div>
                        <div className="ept-ledger-amount">{fmtMoney(al.total_amount, currency)}</div>
                    </div>
                ))}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* BonusesLedger                                                         */
/* ------------------------------------------------------------------ */
function BonusesLedger({ bonuses, employeeId, currency, onCreated, t }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ amount: '', effective_period: '', reason: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSave = async () => {
        const parsedAmount = parseFloat(form.amount);
        if (!parsedAmount || parsedAmount <= 0) {
            setError(t('invalid_amount', {}, 'Enter a valid amount'));
            return;
        }
        setSaving(true);
        setError('');
        try {
            await employeeV2Service.createBonus(employeeId, {
                amount: parsedAmount,
                effective_period: form.effective_period || null,
                reason: form.reason || null,
            });
            setSaving(false);
            setOpen(false);
            setForm({ amount: '', effective_period: '', reason: '' });
            await onCreated();
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    const recent = (bonuses || []).slice(0, 5);

    return (
        <GlassCard veil className="ept-card" data-testid="bonuses-ledger">
            <div className="ept-card-header">
                <span className="ept-card-title">{t('bonuses', {}, 'Bonuses')}</span>
                <button
                    type="button"
                    className="ept-add-btn"
                    data-testid="add-bonus-btn"
                    onClick={() => { setError(''); setOpen(true); }}
                    aria-label={t('add_bonus', {}, 'Add bonus')}
                >
                    <PlusIcon />
                    {t('add', {}, 'Add')}
                </button>
            </div>
            {recent.length === 0
                ? <div className="ept-empty">{t('no_bonuses', {}, 'No bonuses recorded')}</div>
                : recent.map((b) => (
                    <div key={b.bonus_id} className="ept-ledger-item">
                        <div className="ept-ledger-left">
                            <div className="ept-ledger-desc">{b.reason || t('bonus', {}, 'Bonus')}</div>
                            <div className="ept-ledger-date">{b.effective_period}</div>
                        </div>
                        <div className="ept-ledger-amount">{fmtMoney(b.amount, currency)}</div>
                    </div>
                ))}

            <EditSheet
                testId="bonus-sheet"
                open={open}
                onClose={() => setOpen(false)}
                title={t('add_bonus', {}, 'Add Bonus')}
            >
                {error && <div className="ept-err">{error}</div>}
                <div className="ept-field">
                    <label className="ept-field-lbl">{t('amount', {}, 'Amount')}</label>
                    <input
                        className="ept-input"
                        data-testid="bonus-amount"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={form.amount}
                        placeholder="0.00"
                        onChange={(e) => setF('amount', e.target.value)}
                    />
                </div>
                <div className="ept-field">
                    <label className="ept-field-lbl">{t('effective_period', {}, 'Effective Period')}</label>
                    <input
                        className="ept-input"
                        data-testid="bonus-period"
                        type="month"
                        value={form.effective_period}
                        onChange={(e) => setF('effective_period', e.target.value)}
                    />
                </div>
                <div className="ept-field">
                    <label className="ept-field-lbl">{t('reason', {}, 'Reason')}</label>
                    <input
                        className="ept-input"
                        data-testid="bonus-reason"
                        type="text"
                        value={form.reason}
                        onChange={(e) => setF('reason', e.target.value)}
                    />
                </div>
                <div className="ept-actions">
                    <button type="button" className="ept-btn-cancel" onClick={() => setOpen(false)}>
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="ept-btn-save"
                        data-testid="bonus-save-btn"
                        disabled={saving}
                        onClick={handleSave}
                    >
                        {saving && <span className="ept-spinner" aria-hidden="true" />}
                        {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
                    </button>
                </div>
            </EditSheet>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* EmployeePayrollTab                                                    */
/* ------------------------------------------------------------------ */
export default function EmployeePayrollTab({ employeeId, currency }) {
    ensureCss();
    const { t, isRTL } = useLanguage();
    const { push } = useNav();

    const [status, setStatus] = useState('loading');
    const [payrunList, setPayrunList] = useState({ to_run: [], runs: [] });
    const [payslips, setPayslips] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [allowances, setAllowances] = useState([]);
    const [bonuses, setBonuses] = useState([]);
    const [sessions, setSessions] = useState([]);

    const fetchRef = useRef(0);

    const loadAll = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const [prl, slips, advs, alwns, bnses, sess] = await Promise.all([
                // F13 Task 1 — /payrun now requires portal:payroll view rights
                // (backend/app/routers/payrun.py). This tab is reached through
                // portal:employees, which a tenant can grant WITHOUT payroll
                // access, so a 403 here must degrade the pay-run section only
                // — inside Promise.all it would otherwise blank the entire tab
                // (payslips, advances, allowances, bonuses, sessions and all).
                payrunService.list().catch(() => ({ to_run: [], runs: [] })),
                employeeV2Service.listPayslips(employeeId),
                employeeService.getEmployeeAdvances(employeeId),
                employeeService.getEmployeeAllowances(employeeId),
                employeeV2Service.listBonuses(employeeId),
                sessionService.getActiveSessions(),
            ]);
            if (id !== fetchRef.current) return;
            setPayrunList(prl || { to_run: [], runs: [] });
            setPayslips(Array.isArray(slips) ? slips : []);
            setAdvances(Array.isArray(advs) ? advs : []);
            setAllowances(Array.isArray(alwns) ? alwns : []);
            setBonuses(Array.isArray(bnses) ? bnses : []);
            setSessions(Array.isArray(sess) ? sess : []);
            setStatus('ready');
        } catch {
            if (id !== fetchRef.current) return;
            setStatus('error');
        }
    }, [employeeId]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const reloadAdvances = useCallback(async () => {
        const advs = await employeeService.getEmployeeAdvances(employeeId);
        setAdvances(Array.isArray(advs) ? advs : []);
    }, [employeeId]);

    const reloadBonuses = useCallback(async () => {
        const bnses = await employeeV2Service.listBonuses(employeeId);
        setBonuses(Array.isArray(bnses) ? bnses : []);
    }, [employeeId]);

    return (
        <div className="ept-wrap" dir={isRTL ? 'rtl' : 'ltr'} data-testid="employee-payroll-tab">
            <SurfaceStates status={status} onRetry={loadAll}>
                {status === 'ready' && (
                    <>
                        {/* Current period */}
                        <div className="ept-section-hdr">
                            <span className="ept-section-title">{t('current_pay_period', {}, 'Current Pay Period')}</span>
                        </div>
                        <CurrentPeriodCard
                            payrunList={payrunList}
                            employeeId={employeeId}
                            currency={currency}
                            t={t}
                            onPushDetail={(id) => push('payrun-detail', { payRunId: id })}
                        />

                        {/* Pay history */}
                        <div className="ept-section-hdr">
                            <span className="ept-section-title">{t('pay_history', {}, 'Pay History')}</span>
                        </div>
                        <PayHistorySection payslips={payslips} currency={currency} t={t} />

                        {/* Ledgers */}
                        <div className="ept-section-hdr">
                            <span className="ept-section-title">{t('ledgers', {}, 'Ledgers')}</span>
                        </div>
                        <AdvancesLedger
                            advances={advances}
                            employeeId={employeeId}
                            currency={currency}
                            sessions={sessions}
                            onCreated={reloadAdvances}
                            t={t}
                        />
                        <AllowancesLedger
                            allowances={allowances}
                            currency={currency}
                            t={t}
                        />
                        <BonusesLedger
                            bonuses={bonuses}
                            employeeId={employeeId}
                            currency={currency}
                            onCreated={reloadBonuses}
                            t={t}
                        />
                    </>
                )}
            </SurfaceStates>
        </div>
    );
}
